
import { Movie } from "@/types/movie";
import { logAction } from "@/utils/debugLogger";

export const useCardInteractions = (
  movie: Movie,
  currentTab?: string,
  isSearchResult: boolean = false,
  shouldDebug: boolean = false,
  hasPendingAction: boolean = false,
  onOpenMovieDetails?: (movieId: string) => void,
  commitToggleFavorite?: (movie: Movie) => Promise<void>,
  commitTogglePlanToWatch?: (movie: Movie) => Promise<void>,
  toggleFavorite?: (movieId: number, currentSection?: string, movieData?: Movie) => Promise<void>,
  togglePlanToWatch?: (movieId: number, currentSection?: string, movieData?: Movie) => Promise<void>
) => {
  const handleOpenDialog = () => {
    if (!movie.tmdbId) {
      console.warn('Cannot open dialog for movie without tmdbId:', movie);
      return;
    }
    
    if (shouldDebug) {
      logAction(`Opening dialog for movie: ${movie.title}`, {
        tmdbId: movie.tmdbId,
        isSearchResult,
        currentTab
      });
    }
    
    if (onOpenMovieDetails) {
      const movieId = movie.tmdbId.toString();
      onOpenMovieDetails(movieId);
    }
  };

  // FIXED: Simplified favorite handler - use appropriate action based on context
  const handleToggleFavorite = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!movie.tmdbId) {
      console.warn('Cannot toggle favorite for movie without tmdbId:', movie);
      return;
    }
    
    if (shouldDebug) {
      logAction(`Toggling favorite for: ${movie.title}`, {
        currentState: movie.favorite,
        isSearchResult,
        hasPendingAction,
        currentTab
      });
    }

    try {
      if (isSearchResult && commitToggleFavorite) {
        // Search results: immediate commit to database
        console.log(`🔄 Committing favorite toggle for search result: ${movie.title}`);
        await commitToggleFavorite(movie);
        console.log(`✅ Successfully committed favorite toggle for: ${movie.title}`);
      } else if (toggleFavorite) {
        // All other contexts: use unified toggle system with undo functionality
        console.log(`🔄 Toggling favorite for: ${movie.title} in context: ${currentTab}`);
        await toggleFavorite(movie.tmdbId, currentTab, movie);
        console.log(`✅ Successfully toggled favorite for: ${movie.title}`);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  // FIXED: Simplified plan to watch handler - use appropriate action based on context
  const handleTogglePlanToWatch = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!movie.tmdbId) {
      console.warn('Cannot toggle plan to watch for movie without tmdbId:', movie);
      return;
    }
    
    if (shouldDebug) {
      logAction(`Toggling plan to watch for: ${movie.title}`, {
        currentState: movie.planToWatch,
        isSearchResult,
        hasPendingAction,
        currentTab
      });
    }

    try {
      if (isSearchResult && commitTogglePlanToWatch) {
        // Search results: immediate commit to database
        console.log(`🔄 Committing plan to watch toggle for search result: ${movie.title}`);
        await commitTogglePlanToWatch(movie);
        console.log(`✅ Successfully committed plan to watch toggle for: ${movie.title}`);
      } else if (togglePlanToWatch) {
        // All other contexts: use unified toggle system with undo functionality
        console.log(`🔄 Toggling plan to watch for: ${movie.title} in context: ${currentTab}`);
        await togglePlanToWatch(movie.tmdbId, currentTab, movie);
        console.log(`✅ Successfully toggled plan to watch for: ${movie.title}`);
      }
    } catch (error) {
      console.error('Error toggling plan to watch:', error);
    }
  };

  return {
    handleOpenDialog,
    handleToggleFavorite,
    handleTogglePlanToWatch
  };
};
